/**
 * Intercepteur d'authentification global
 * Capture les erreurs 401 de toutes les requêtes fetch et déclenche un logout automatique
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AUTH_ERROR_EVENT, isTokenExpired } from '../utils/api';

const AuthInterceptor = ({ children }) => {
  const { logout, user } = useAuth();
  const originalFetch = useRef(window.fetch);

  useEffect(() => {
    // Sauvegarder la référence originale de fetch
    const originalFetchFn = originalFetch.current;

    // Surcharger window.fetch pour intercepter les erreurs 401
    window.fetch = async (...args) => {
      const [url, options = {}] = args;
      
      // Vérifier si c'est une requête authentifiée (avec token dans le header)
      const token = localStorage.getItem('portalToken');
      const isAuthRequest = options.headers?.Authorization || 
                           (token && url.startsWith('/api/') && !url.includes('/api/auth/login'));
      
      // Vérifier si le token est expiré avant d'envoyer la requête
      if (isAuthRequest && token && isTokenExpired(token)) {
        console.log('[AuthInterceptor] Token expiré détecté, déclenchement logout');
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { reason: 'expired' } }));
        return Promise.reject(new Error('Session expirée'));
      }
      
      try {
        const response = await originalFetchFn.apply(window, args);
        
        // Intercepter les erreurs 401 sur les requêtes API
        if (response.status === 401 && url.startsWith('/api/') && !url.includes('/api/auth/login')) {
          console.log('[AuthInterceptor] Erreur 401 détectée sur:', url);
          window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { reason: 'unauthorized' } }));
        }
        
        return response;
      } catch (error) {
        throw error;
      }
    };

    // Cleanup: restaurer la fonction fetch originale
    return () => {
      window.fetch = originalFetchFn;
    };
  }, []);

  // Écouter les événements d'erreur d'authentification
  useEffect(() => {
    const handleAuthError = (event) => {
      console.log('[AuthInterceptor] Événement AUTH_ERROR reçu:', event.detail);
      if (user) {
        logout('session_expired');
      }
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
  }, [user, logout]);

  return children;
};

export default AuthInterceptor;
